import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  Flex,
  Spinner,
  useToast,
  Divider,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormikInput from '../../components/formikinput';
import { formatCurrency } from '../../utils/formatCurrency';
import useAddPaymentMutation from '../../api/mutations/useAddPaymentMutation';
import useGetSellers from '../../api/queries/useGetSellers';
import FormikSearchAbleSelect from '../../components/formikSearchAbleSelect';
import { useUserContext } from '../../contexts/userContexts';
import useGetBalance from '../../api/queries/useGetBalance';

const AddPayment = ({ isOpen, onClose, size = 'xl' }) => {
  const toast = useToast();
  const [isAddDisabled, setIsAddDisabled] = useState(true);
  const { user } = useUserContext();

  const [totalAmount, setTotalAmount] = useState(0);
  const [amountError, setAmountError] = useState('');

  const { data, isLoading, error } = useGetSellers({
    page: 1,
    limit: 10000000,
    sortColumn: 'date_created',
    sortOrder: 'desc',
  });

  const sellers = data?.data || [];

  console.log('User', user);
  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      payee_id: '', // seller is now payee
      amount: '',
      paidFromBank: '', // renamed from bank to match payload
      reference: '',
      type: 'payment',
    },
    validationSchema: Yup.object({
      payee_id: Yup.string().required('This field is required'),
      amount: Yup.number()
        .typeError('Amount must be a number')
        .positive('Amount must be positive')
        .required('Amount is required'),
      paidFromBank: Yup.string().required('Bank is required'),
      reference: Yup.string().optional(),
    }),
    onSubmit: values => addPayment(values),
  });

  const handleClose = () => {
    formik.resetForm();
    setTotalAmount(0);
    setAmountError('');
    onClose();
  };

  const sellerId = formik?.values?.payee_id;
  const balanceQuery = useGetBalance(sellerId);

  // console.log('Transaction IDs:', balanceQuery?.data?.data?.map(item => item.transaction_id));
  useEffect(() => {
    if (Array.isArray(balanceQuery?.data)) {
      const total = balanceQuery.data.reduce((sum, item) => {
        const type = item?.transaction_id?.transaction_type_id?.name;
        const amount = Number(item?.amount) || 0;

        // + Positive categories: seller-commission, dry-cleaning-cost-removed
        if (type === "seller-commission" || type === "seller_commission" || type === "dry-cleaning-cost-removed") {
          return sum + amount;
        }

        // - All others are negative
        return sum - Math.abs(amount);
      }, 0);

      setTotalAmount(total);
      console.log("🔥 Final Balance:", total);
    }
  }, [balanceQuery]);
  
  
  
  // useEffect(() => {
  //   if (balanceQuery?.data && Array.isArray(balanceQuery.data)) {
  //     // console.log('Balance query', balanceQuery?.data);

  //     const total = balanceQuery.data.reduce((sum, item) => {
  //       const amount = Number(item.amount) || 0;
  //       return sum + amount;
  //     }, 0);
  //     setTotalAmount(total);
  //     console.log('📊 Seller Total Balance:', total);
  //   }
  // }, [balanceQuery.data]);

  // Watch formik amount value
  useEffect(() => {
    const enteredAmount = Math.round((Number(formik.values.amount) || 0) * 100) / 100;
    const roundedTotal = Math.round(totalAmount * 100) / 100;
    if (enteredAmount > roundedTotal) {
      setAmountError("Amount exceeds seller's Balance");
      setIsAddDisabled(true);
    } else {
      setAmountError('');
      const { payee_id, amount, paidFromBank } = formik.values;
      setIsAddDisabled(!(payee_id && amount && paidFromBank));
    }
  }, [formik.values.amount, totalAmount, formik.values]);
  // Mutation Hook
  const addPaymentMutation = useAddPaymentMutation({
    onSuccess: res => {
      toast({
        title: 'Payment Added Successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();
    },
    onError: err => {
      console.error(err);
      toast({
        title: 'Failed to add payment',
        description: err.response?.data?.message || 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  // Submit Payment
  const addPayment = values => {
    // console.log('Values', values);
    try {
      const payload = {
        payer_id: user.info._id, // Use logged-in user's ID as payer
        payee_id: values.payee_id,
        amount: Number(values.amount),
        paidFromBank: values.paidFromBank,
        reference: values.reference || undefined,
        type: values.type,
      };
      console.log('Complete Payload:', payload);
      addPaymentMutation.mutate(payload);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={size}>
      <ModalOverlay />
      <ModalContent maxWidth={'800px'} borderRadius={'16px'} p={4}>
        <ModalBody gap={6} display={'flex'} flexDirection={'column'}>
          <Text fontSize={'24px'} fontWeight={'semibold'} color={'black'}>
            Add Payment
          </Text>
          <Divider borderWidth={'1px'} color={'gray.200'} />

          <Box>
            <Flex justifyContent={'space-between'}>
              <Text fontSize={'16px'} fontWeight={'semibold'} pb={2}>
                Payment Details
              </Text>
              {sellerId && (
                <Text
                  fontSize="16px"
                  fontWeight="semibold"
                  pb={2}
                  color={totalAmount > 0 ? 'green.500' : 'red.500'}
                >
                  Total Balance: {formatCurrency(totalAmount)}
                </Text>
              )}
            </Flex>
            <Box borderRadius={'6px'} p={4} border={'1px solid gray.200'}>
              <Flex gap={5} mb={4} direction={{ base: 'column', md: 'row' }}>
                <FormikSearchAbleSelect
                  formik={formik}
                  name="payee_id"
                  label="Pick a Seller*"
                  placeholder="Select a seller"
                  isLoading={isLoading}
                >
                  {sellers.map(seller => (
                    <option key={seller._id} value={seller._id}>
                      {`${seller.first_name} ${seller.surname || ''}`}
                    </option>
                  ))}
                </FormikSearchAbleSelect>
                <FormikSearchAbleSelect
                  formik={formik}
                  name="payee_id"
                  label="Pick a Seller ID*"
                  placeholder="Select a seller ID"
                  isLoading={isLoading}
                >
                  {sellers.map(seller => (
                    <option key={seller._id} value={seller._id}>
                      {`${seller?.id}`}
                    </option>
                  ))}
                </FormikSearchAbleSelect>
              </Flex>

              <Flex gap={5} mb={4} direction={{ base: 'column', md: 'row' }}>
                <Box flex="1">
                  <FormikInput
                    formik={formik}
                    name="paidFromBank"
                    label="Paid From Bank *"
                    placeholder="Enter bank name"
                  />
                </Box>

                <Box flex="1">
                  <FormikInput
                    formik={formik}
                    name="amount"
                    label="Amount *"
                    type="number"
                    placeholder="Enter amount"
                  />
                  {amountError && (
                    <Text color="red" fontSize="sm" mt={1}>
                      {amountError}
                    </Text>
                  )}
                </Box>
              </Flex>

              <Flex>
                <FormikInput
                  formik={formik}
                  name="reference"
                  label="Reference (Optional)"
                  placeholder="Enter reference"
                  flex={1}
                />
              </Flex>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Flex w={'100%'} justify={'flex-end'} gap={4}>
            <Button
              bg={'white'}
              border={'1px solid #000000'}
              color="#000000"
              borderRadius={8}
              paddingInline={16}
              textTransform={'uppercase'}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={formik.handleSubmit}
              isDisabled={isAddDisabled || addPaymentMutation.isLoading}
              bg={'#000000'}
              color="#FFFFFF"
              borderRadius={8}
              paddingInline={16}
              textTransform={'uppercase'}
              border={'1px solid #FFFFFF'}
              _disabled={{
                bg: 'grey.500',
                border: '1px solid #FFFFFF',
                cursor: 'initial',
                _hover: {
                  bg: 'grey.500',
                  border: '1px solid #FFFFFF',
                  cursor: 'initial',
                },
              }}
            >
              {addPaymentMutation.isLoading ? <Spinner /> : 'Add'}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPayment;
